import { useAuth } from '@/lib/auth'
import { type QueryKey, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { getEcho } from '.'

type EventHandler = (payload: unknown) => void

/**
 * A query key, or a query-key factory that takes the channel's organisation id.
 * SDK query-key helpers (e.g. `customerWalletShowQueryKey`) are exactly this
 * factory shape, so they can be passed by reference.
 */
type QueryKeyOrFactory = QueryKey | ((organisation: string) => QueryKey)

/**
 * An event handler is either a callback, or a list of query keys to invalidate.
 * The list form is the common case — "this event changed server state, refetch".
 */
type ChannelEventHandler = EventHandler | ReadonlyArray<QueryKeyOrFactory>

/**
 * Every private channel in the app is `organisations.{organisationId}.<resource>`
 * (see `.claude/rules/broadcasting.md`), so the organisation id is the second
 * segment. Returns null for any other shape — factory handlers then receive ''.
 */
function organisationIdFromChannel(channelName: string): string | null {
	const segments = channelName.split('.')
	return segments[0] === 'organisations' && segments[1] ? segments[1] : null
}

/**
 * Subscribe to a private channel and dispatch incoming events to the supplied
 * handlers. Re-subscribes whenever channel or event identities change; cleans
 * up on unmount.
 *
 * Handler keys are the over-the-wire broadcast names (the backend's
 * `broadcastAs()` / the generated SDK enum values, e.g. `walletEvent.Credited`).
 * Echo needs a leading `.` to treat a name as literal (no namespace, no
 * dot→backslash mangling), so we add it here — callers never deal with it.
 *
 * A handler value is either a callback, or a list of query keys to invalidate
 * when the event fires. Query-key factories in the list are auto-resolved with
 * the channel's organisation id, so SDK helpers can be passed by reference:
 *
 *   usePrivateChannel(broadcastChannel.OrganisationWallet({ organisation }), {
 *     [walletEvent.Credited]: [customerWalletShowQueryKey, customerWalletTransactionsQueryKey],
 *   })
 *
 * Handlers are stored in a ref so the wrapper listeners always call the latest
 * render's handler — a handler closure captured at subscription time would
 * otherwise go stale on every re-render.
 */
const toEchoEvent = (event: string): string => (event.startsWith('.') ? event : `.${event}`)
export function usePrivateChannel(
	channelName: string | null,
	handlers: Record<string, ChannelEventHandler>,
): void {
	const { getAccessToken } = useAuth()
	const queryClient = useQueryClient()
	const handlersRef = useRef(handlers)
	handlersRef.current = handlers

	const eventNames = Object.keys(handlers).sort().join(',')

	useEffect(() => {
		if (!channelName) {
			return
		}
		const echo = getEcho(getAccessToken)
		const channel = echo.private(channelName)
		const organisationId = organisationIdFromChannel(channelName) ?? ''

		const wrappers: Array<[string, EventHandler]> = []
		for (const event of Object.keys(handlersRef.current)) {
			const wrapper: EventHandler = (payload) => {
				const handler = handlersRef.current[event]
				if (typeof handler === 'function') {
					handler(payload)
					return
				}
				for (const keyOrFactory of handler) {
					const queryKey = typeof keyOrFactory === 'function' ? keyOrFactory(organisationId) : keyOrFactory
					queryClient.invalidateQueries({ queryKey })
				}
			}
			const echoEvent = toEchoEvent(event)
			channel.listen(echoEvent, wrapper)
			wrappers.push([echoEvent, wrapper])
		}

		return () => {
			for (const [echoEvent, wrapper] of wrappers) {
				channel.stopListening(echoEvent, wrapper)
			}
			echo.leave(channelName)
		}
	}, [channelName, eventNames, getAccessToken, queryClient])
}
